import { useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import { Tooltip as Base } from '@base-ui/react/tooltip';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { Kbd } from './Kbd';

/** The first tooltip waits this long (ms); the others open at once while one is open or was a moment ago. */
export const TOOLTIP_DELAY = 500;
/** A tooltip closes at once when the pointer leaves its control. */
export const TOOLTIP_CLOSE_DELAY = 0;
/** Another tooltip opens with no delay if the previous one closed less than this long ago (ms). */
export const TOOLTIP_WARM_TIMEOUT = 400;

/**
 * Mount once at the root (providers.tsx): `<AppTooltipProvider>…</AppTooltipProvider>`. It is the shared timer of every
 * `Tooltip` and `IconButton`: 500 ms before the first, none for the next one within 400 ms of the last.
 */
export function AppTooltipProvider({ children }: { children?: ReactNode }) {
  return (
    <Base.Provider
      delay={TOOLTIP_DELAY}
      closeDelay={TOOLTIP_CLOSE_DELAY}
      timeout={TOOLTIP_WARM_TIMEOUT}
    >
      {children}
    </Base.Provider>
  );
}

/** A finger cannot hover: a coarse pointer without hover gets no tooltip (a tap must not leave one stuck open). */
const NO_HOVER = '(hover: none) and (pointer: coarse)';
const noHoverQuery = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(NO_HOVER)
    : null;
const subscribeNoHover = (notify: () => void) => {
  const query = noHoverQuery();
  query?.addEventListener?.('change', notify);
  return () => query?.removeEventListener?.('change', notify);
};
const useNoHover = () =>
  useSyncExternalStore(
    subscribeNoHover,
    () => noHoverQuery()?.matches ?? false,
    () => false,
  );

/** Hover/focus label of a control, with its shortcut; the control keeps its own accessible name. Opacity-only fade (`.os-tip`). */
export function Tooltip({
  label,
  shortcut,
  side = 'top',
  children,
}: {
  label: string;
  shortcut?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  children: ReactElement;
}) {
  const touch = useNoHover();
  return (
    <Base.Root disabled={touch}>
      <Base.Trigger render={children} />
      <Base.Portal>
        <Base.Positioner
          side={side}
          sideOffset={8}
          className="z-(--layer-tooltip)"
          {...{ [CANVAS_UI]: '' }}
        >
          <Base.Popup className="os-tip flex items-center gap-2 rounded-full bg-fg px-2.5 py-1 font-sans text-xs font-medium text-bg shadow-md">
            <span>{label}</span>
            {shortcut && (
              <Kbd className="border-transparent bg-fg-muted/30 text-bg">{shortcut}</Kbd>
            )}
          </Base.Popup>
        </Base.Positioner>
      </Base.Portal>
    </Base.Root>
  );
}

/** The bare Base UI provider, for a test or a story that needs other delays; the app mounts `AppTooltipProvider`. */
export const TooltipProvider = Base.Provider;
